/**
 * Backend Warmup Utility
 *
 * Preemptively wakes up Cloud Run instances to reduce cold start latency.
 * Called as early as possible in the app lifecycle.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

let warmupStarted = false
let warmupPromise: Promise<boolean> | null = null

/**
 * Ping the backend to wake up Cloud Run instances.
 * Retries multiple times to ensure the server is fully awake.
 * Safe to call multiple times - only the first call does anything.
 */
export function warmupBackend(): Promise<boolean> {
  // Only run once
  if (warmupStarted) {
    return warmupPromise || Promise.resolve(true)
  }

  warmupStarted = true
  console.log('[Warmup] Waking up backend services...')

  // Retry function with delay
  async function attemptWarmup(attempt: number, maxAttempts: number): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/health`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        signal: AbortSignal.timeout(10000), // 10 second timeout per attempt
      })

      if (response.ok) {
        console.log('[Warmup] Backend is ready')
        return true
      }

      if (response.status === 503 && attempt < maxAttempts) {
        console.log(`[Warmup] Server starting up, retry ${attempt}/${maxAttempts}...`)
        await new Promise((resolve) => setTimeout(resolve, 3000)) // Wait 3 seconds
        return attemptWarmup(attempt + 1, maxAttempts)
      }

      console.log('[Warmup] Backend responded with status:', response.status)
      return false
    } catch (error) {
      const err = error as Error
      if (attempt < maxAttempts) {
        console.log(`[Warmup] Request failed, retry ${attempt}/${maxAttempts}...`, err.message)
        await new Promise((resolve) => setTimeout(resolve, 3000))
        return attemptWarmup(attempt + 1, maxAttempts)
      }
      console.log('[Warmup] Backend warmup failed after retries:', err.message)
      return false
    }
  }

  warmupPromise = attemptWarmup(1, 5) // Try up to 5 times (15 seconds total)

  return warmupPromise
}

/**
 * Check if warmup has completed successfully
 */
export function isBackendWarmedUp(): boolean {
  return warmupStarted && warmupPromise !== null
}

/**
 * Wait for warmup to complete (with timeout)
 */
export async function waitForWarmup(timeoutMs = 5000): Promise<boolean> {
  if (!warmupPromise) {
    return false
  }

  try {
    const result = await Promise.race([
      warmupPromise,
      new Promise<boolean>((resolve) => setTimeout(() => resolve(false), timeoutMs)),
    ])
    return result
  } catch {
    return false
  }
}
